import React, { useState, useMemo } from "react";
import { useQuery, useAction } from "wasp/client/operations";
import { getRetailStores, deleteRetailStore } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { Link, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../../client/components/Sidebar";
import { useLocationContext } from "../../client/contexts/LocationContext";
import { useRequireSectionVisible } from "../../client/hooks/useRequireSectionVisible";
import {
  EnhancedActionButton,
  EnhancedIconButton,
  enhancedPageLayoutStyles,
  useResponsiveLayout,
} from "../../components/ui/enhanced-page-layout";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { Button } from "../../components/ui/button";
import { cn } from "../../lib/utils";
import {
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  Building2,
  Tag,
  Phone,
  Clock,
  Star,
  MapPin,
  Grid3X3,
  List,
  Table,
} from "lucide-react";

export default function RetailPage() {
  useRequireSectionVisible("showRetail");
  const { data: user } = useAuth();
  const { selectedLocation } = useLocationContext();
  const navigate = useNavigate();
  const deleteRetailStoreFn = useAction(deleteRetailStore);

  // State for filters and view mode
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [selectedStoreType, setSelectedStoreType] = useState<string>("all");
  const [sortBy, setSortBy] = useState<
    "newest" | "oldest" | "title" | "verified"
  >("newest");
  // Use responsive layout hook
  const {
    viewMode,
    setViewMode,
    isFilterExpanded,
    setIsFilterExpanded,
    isMobile,
    shouldCollapseSidebar,
    hideViewSwitcher,
  } = useResponsiveLayout("table");

  // Fetch stores
  const {
    data: stores = [],
    isLoading,
    error,
  } = useQuery(getRetailStores, {
    locationId: selectedLocation?.id || "",
    filters: {
      category: selectedCategory === "all" ? undefined : selectedCategory,
      storeType: selectedStoreType === "all" ? undefined : selectedStoreType,
    },
  });

  // Filter and sort stores
  const filteredStores = useMemo(() => {
    let filtered = stores as any[];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (store: any) =>
          store.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          store.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          store.metadata?.storeType
            ?.toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    // Sort
    switch (sortBy) {
      case "newest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        break;
      case "oldest":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        );
        break;
      case "title":
        filtered = [...filtered].sort((a: any, b: any) =>
          a.title.localeCompare(b.title),
        );
        break;
      case "verified":
        filtered = [...filtered].sort(
          (a: any, b: any) =>
            (b.metadata?.isVerified ? 1 : 0) - (a.metadata?.isVerified ? 1 : 0),
        );
        break;
    }

    return filtered;
  }, [stores, searchTerm, selectedCategory, selectedStoreType, sortBy]);

  const handleDelete = async (storeId: string) => {
    if (window.confirm("Are you sure you want to delete this store?")) {
      try {
        await deleteRetailStoreFn({ storeId });
        // The query will automatically refetch due to Wasp's reactivity
      } catch (error) {
        console.error("Error deleting store:", error);
        alert("Error deleting store. Please try again.");
      }
    }
  };

  // Authentication check
  if (!user) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Authentication Required
            </h2>
            <p className="text-gray-600">
              You must be logged in to view retail stores.
            </p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const locationName = selectedLocation?.name || "Ottawa University";

  const getCategoryColor = (category: string) => {
    const colors = {
      CLOTHING: "bg-accent/10 text-accent border border-accent/20",
      ELECTRONICS: "bg-primary/10 text-primary border border-primary/20",
      HOME_GARDEN: "bg-success/10 text-success border border-success/20",
      SPORTS: "bg-secondary/10 text-secondary border border-secondary/20",
      BOOKS: "bg-warning/10 text-warning border border-warning/20",
      BEAUTY: "bg-secondary/10 text-secondary border border-secondary/20",
      TOYS: "bg-destructive/10 text-destructive border border-destructive/20",
      AUTOMOTIVE: "bg-muted text-muted-foreground border border-border",
      FOOD_BEVERAGE: "bg-warning/10 text-warning border border-warning/20",
      HEALTHCARE: "bg-success/10 text-success border border-success/20",
      PROFESSIONAL_SERVICES:
        "bg-muted text-muted-foreground border border-border",
      OTHER: "bg-primary/10 text-primary border border-primary/20",
    };
    return (
      colors[category as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const getStoreTypeColor = (storeType: string) => {
    const colors = {
      RETAIL_STORE: "bg-primary/10 text-primary border border-primary/20",
      RESTAURANT: "bg-secondary/10 text-secondary border border-secondary/20",
      SERVICE_SHOP: "bg-success/10 text-success border border-success/20",
      BOUTIQUE: "bg-secondary/10 text-secondary border border-secondary/20",
      DEPARTMENT_STORE: "bg-accent/10 text-accent border border-accent/20",
      SPECIALTY_STORE: "bg-warning/10 text-warning border border-warning/20",
      OTHER: "bg-muted text-muted-foreground border border-border",
    };
    return (
      colors[storeType as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  const getAvailabilityColor = (availability: string) => {
    const colors = {
      AVAILABLE: "bg-success/10 text-success border border-success/20",
      LIMITED: "bg-warning/10 text-warning border border-warning/20",
      OUT_OF_STOCK:
        "bg-destructive/10 text-destructive border border-destructive/20",
    };
    return (
      colors[availability as keyof typeof colors] ||
      "bg-muted text-muted-foreground border border-border"
    );
  };

  return (
    <DashboardLayout
      forceSidebarCollapse={shouldCollapseSidebar}
      showViewSwitcher={!hideViewSwitcher}
      activeView={viewMode}
      onViewChange={setViewMode}
    >
      <div className="w-full h-full flex flex-col">
        {/* Fixed Main Header */}
        <div className="fixed top-[var(--header-height)] left-0 lg:left-[var(--sidebar-width)] right-0 z-20 bg-white border-b border-gray-200 transition-all duration-300">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Businesses
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Discover businesses and services at {locationName} ({filteredStores.length} businesses)
                </p>
              </div>
              <div className="flex items-center gap-3">
                <EnhancedActionButton
                  onClick={() => setIsFilterExpanded(!isFilterExpanded)}
                  icon={Filter}
                  label=""
                  variant={isFilterExpanded ? "primary" : "secondary"}
                  className="hidden lg:flex"
                />

                {/* Mobile Filter Button */}
                <Sheet open={isFilterExpanded && isMobile} onOpenChange={setIsFilterExpanded}>
                  <EnhancedActionButton
                    onClick={() => setIsFilterExpanded(true)}
                    icon={Filter}
                    label=""
                    variant={isFilterExpanded ? "primary" : "secondary"}
                    className="lg:hidden"
                  />
                  <SheetContent side="right" className="w-[300px] sm:w-[400px]">
                    <SheetHeader>
                      <SheetTitle>Filters</SheetTitle>
                    </SheetHeader>
                    <div className="py-6 space-y-6">
                      <div className="space-y-2">
                        <label className="text-sm font-medium">Search</label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="text"
                            placeholder="Search businesses..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Category</label>
                        <select
                          value={selectedCategory}
                          onChange={(e) => setSelectedCategory(e.target.value)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="all">All Categories</option>
                          <option value="CLOTHING">Clothing</option>
                          <option value="ELECTRONICS">Electronics</option>
                          <option value="HOME_GARDEN">Home & Garden</option>
                          <option value="SPORTS">Sports</option>
                          <option value="BOOKS">Books</option>
                          <option value="BEAUTY">Beauty</option>
                          <option value="FOOD_BEVERAGE">Food & Beverage</option>
                        </select>
                      </div>

                      <div className="space-y-2">
                        <label className="text-sm font-medium">Sort By</label>
                        <select
                          value={sortBy}
                          onChange={(e) => setSortBy(e.target.value as any)}
                          className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                          <option value="newest">Newest First</option>
                          <option value="oldest">Oldest First</option>
                          <option value="title">Name A-Z</option>
                          <option value="verified">Verified First</option>
                        </select>
                      </div>

                      <Button 
                        className="w-full" 
                        onClick={() => setIsFilterExpanded(false)}
                      >
                        Apply Filters
                      </Button>
                    </div>
                  </SheetContent>
                </Sheet>
                {user?.isAdmin && (
                  <EnhancedActionButton
                    onClick={() => {
                      if (selectedLocation?.locationCode) {
                        navigate(
                          `/locations/${selectedLocation.locationCode}/retail/add`,
                        );
                      } else {
                        navigate("/retail/add");
                      }
                    }}
                    icon={Plus}
                    label="Add Business"
                    hideLabelOnMobile
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Fixed Table Header (Conditional - Desktop only) */}
        {!isLoading && !error && stores.length > 0 && viewMode === "table" && (
          <div 
            className={cn(
              "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-sidebar-hover border-b border-border transition-all duration-300 hidden lg:block",
              isFilterExpanded ? "max-h-[500px]" : "max-h-[45px] overflow-hidden"
            )}
          >
            <div className="px-6 py-3 space-y-3">
              {/* Column Headers */}
              <div className="grid grid-cols-12 gap-4 text-xs font-medium text-content-text-muted uppercase tracking-wider select-none cursor-default">
                <div className="col-span-3">Store</div>
                <div className="col-span-2">Category</div>
                <div className="col-span-2">Type</div>
                <div className="col-span-1">Status</div>
                <div className="col-span-2">Location</div>
                <div className={user?.isAdmin ? "col-span-1" : "col-span-2"}>Author</div>
                {user?.isAdmin && <div className="col-span-1 text-right">Actions</div>}
              </div>

              {/* Column Filters */}
              <div className="grid grid-cols-12 gap-4">
                <div className="col-span-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <input
                      type="text"
                      placeholder="Search stores..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="CLOTHING">Clothing</option>
                    <option value="ELECTRONICS">Electronics</option>
                    <option value="HOME_GARDEN">Home & Garden</option>
                    <option value="SPORTS">Sports</option>
                    <option value="BOOKS">Books</option>
                    <option value="BEAUTY">Beauty</option>
                    <option value="TOYS">Toys</option>
                    <option value="AUTOMOTIVE">Automotive</option>
                    <option value="FOOD_BEVERAGE">Food & Beverage</option>
                    <option value="HEALTHCARE">Healthcare</option>
                    <option value="PROFESSIONAL_SERVICES">
                      Professional Services
                    </option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <select
                    value={selectedStoreType}
                    onChange={(e) => setSelectedStoreType(e.target.value)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="RETAIL_STORE">Retail Store</option>
                    <option value="RESTAURANT">Restaurant</option>
                    <option value="SERVICE_SHOP">Service Shop</option>
                    <option value="BOUTIQUE">Boutique</option>
                    <option value="DEPARTMENT_STORE">Department Store</option>
                    <option value="SPECIALTY_STORE">Specialty Store</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div className="col-span-1">
                  <select
                    value={sortBy === "verified" ? "verified" : "all"}
                    onChange={(e) =>
                      setSortBy(
                        e.target.value === "verified" ? "verified" : "newest",
                      )
                    }
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All</option>
                    <option value="verified">Verified</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <input
                    type="text"
                    placeholder="All units"
                    className="w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div className="col-span-1">
                  <select
                    value={
                      sortBy === "newest" ||
                      sortBy === "oldest" ||
                      sortBy === "title"
                        ? sortBy
                        : "newest"
                    }
                    onChange={(e) => setSortBy(e.target.value as any)}
                    className="appearance-none w-full px-3 py-2 bg-white border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="newest">Newest</option>
                    <option value="oldest">Oldest</option>
                    <option value="title">A-Z</option>
                  </select>
                </div>
                <div className="col-span-1">
                  {/* Actions column - no filter needed */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Fixed Filters (Desktop) */}
        {!isLoading && !error && stores.length > 0 && viewMode !== "table" && (
          <div
            className={cn(
              "fixed top-[calc(var(--header-height)+5rem)] left-0 lg:left-[var(--sidebar-width)] right-0 z-10 bg-white border-b border-gray-200 transition-all duration-300 ease-in-out hidden lg:block",
              isFilterExpanded
                ? "translate-y-0 opacity-100"
                : "-translate-y-full opacity-0 pointer-events-none",
            )}
          >
            <div className="px-4 py-3">
              <div className="flex items-center gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search stores..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                {/* Category Filter */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Categories</option>
                  <option value="CLOTHING">Clothing</option>
                  <option value="ELECTRONICS">Electronics</option>
                  <option value="HOME_GARDEN">Home & Garden</option>
                  <option value="SPORTS">Sports</option>
                  <option value="BOOKS">Books</option>
                  <option value="BEAUTY">Beauty</option>
                  <option value="TOYS">Toys</option>
                  <option value="AUTOMOTIVE">Automotive</option>
                  <option value="FOOD_BEVERAGE">Food & Beverage</option>
                  <option value="HEALTHCARE">Healthcare</option>
                  <option value="PROFESSIONAL_SERVICES">
                    Professional Services
                  </option>
                  <option value="OTHER">Other</option>
                </select>

                {/* Store Type Filter */}
                <select
                  value={selectedStoreType}
                  onChange={(e) => setSelectedStoreType(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="all">All Store Types</option>
                  <option value="RETAIL_STORE">Retail Store</option>
                  <option value="RESTAURANT">Restaurant</option>
                  <option value="SERVICE_SHOP">Service Shop</option>
                  <option value="BOUTIQUE">Boutique</option>
                  <option value="DEPARTMENT_STORE">Department Store</option>
                  <option value="SPECIALTY_STORE">Specialty Store</option>
                  <option value="OTHER">Other</option>
                </select>

                {/* Sort Filter */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as any)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="newest">Newest First</option>
                  <option value="oldest">Oldest First</option>
                  <option value="title">Name A-Z</option>
                  <option value="verified">Verified First</option>
                </select>
              </div>
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex-1 overflow-y-auto transition-[padding] duration-300 ease-in-out",
            isFilterExpanded && !isMobile
              ? (viewMode === "table" ? "pt-[calc(var(--header-height)+11.5rem)]" : "pt-[calc(var(--header-height)+8.5rem)]")
              : "pt-[calc(var(--header-height)+5.5rem)]"
          )}
        >
          <div className="w-full">
            {/* Loading State */}
            {isLoading && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-600 mt-2">Loading stores...</p>
                </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Error Loading Stores
                  </h3>
                  <p className="text-gray-600">Please try again later.</p>
                </div>
              </div>
            )}

            {/* Empty State */}
            {!isLoading && !error && filteredStores.length === 0 && (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    No Stores Found
                  </h3>
                  <p className="text-gray-600">
                    {searchTerm ||
                    selectedCategory !== "all" ||
                    selectedStoreType !== "all"
                      ? "Try adjusting your search or filters to find what you're looking for."
                      : "No retail stores have been added in this location yet."}
                  </p>
                </div>
              </div>
            )}

            {/* Content */}
            {!isLoading && !error && filteredStores.length > 0 && (
              <>
                {/* Cards View - Use enhanced layout with padding */}
                {viewMode === "cards" && (
                  <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {filteredStores.map((store) => (
                        <div
                          key={store.id}
                          className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer"
                          onClick={() => navigate(`/retail/${store.id}`)}
                        >
                          <div className="p-6">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-2">
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                                    store.metadata?.category,
                                  )}`}
                                >
                                  {store.metadata?.category}
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getStoreTypeColor(
                                    store.metadata?.storeType,
                                  )}`}
                                >
                                  {store.metadata?.storeType?.replace("_", " ")}
                                </span>
                              </div>
                              <div className="flex items-center gap-1">
                                {store.metadata?.isVerified && (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                    Verified
                                  </span>
                                )}
                                {store.metadata?.isFeatured && (
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                )}
                              </div>
                            </div>

                            <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2">
                              {store.title}
                            </h3>

                            <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                              {store.description}
                            </p>

                            {store.metadata?.businessHours && (
                              <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                                <Clock className="w-3 h-3" />
                                {store.metadata.businessHours}
                              </div>
                            )}

                            {store.metadata?.unitNumber && (
                              <div className="flex items-center gap-1 text-sm text-gray-500 mb-2">
                                <MapPin className="w-3 h-3" />
                                Unit {store.metadata.unitNumber}
                                {store.metadata?.floor &&
                                  `, Floor ${store.metadata.floor}`}
                              </div>
                            )}

                            {store.metadata?.contactInfo && (
                              <div className="flex items-center gap-1 text-sm text-gray-500 mb-4">
                                <Phone className="w-3 h-3" />
                                {store.metadata.contactInfo}
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="text-sm text-gray-500">
                                by {store.author.name || store.author.email}
                              </div>
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/retail/${store.id}`)
                                  }
                                  icon={Eye}
                                  title="View details"
                                />
                                {user?.isAdmin && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() =>
                                        navigate(`/retail/${store.id}/edit`)
                                      }
                                      icon={Edit}
                                      title="Edit store"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(store.id)}
                                      icon={Trash2}
                                      title="Delete store"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* List View - Use enhanced layout with padding */}
                {viewMode === "list" && (
                  <div className="p-6">
                    <div className="space-y-4">
                      {filteredStores.map((store) => (
                        <div
                          key={store.id}
                          className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow cursor-pointer hover:bg-hover transition-colors"
                          onClick={() => navigate(`/retail/${store.id}`)}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h3 className="text-lg font-semibold text-gray-900">
                                  {store.title}
                                </h3>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getCategoryColor(
                                    store.metadata?.category,
                                  )}`}
                                >
                                  {store.metadata?.category}
                                </span>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded ${getStoreTypeColor(
                                    store.metadata?.storeType,
                                  )}`}
                                >
                                  {store.metadata?.storeType?.replace("_", " ")}
                                </span>
                                {store.metadata?.isVerified && (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                    Verified
                                  </span>
                                )}
                              </div>

                              <p className="text-gray-600 mb-3 line-clamp-2">
                                {store.description}
                              </p>

                              <div className="flex items-center gap-4 text-sm text-gray-500">
                                <span>
                                  by {store.author.name || store.author.email}
                                </span>
                                <span>
                                  {new Date(
                                    store.createdAt,
                                  ).toLocaleDateString()}
                                </span>
                                {store.metadata?.businessHours && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    {store.metadata.businessHours}
                                  </span>
                                )}
                                {store.metadata?.contactInfo && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {store.metadata.contactInfo}
                                  </span>
                                )}
                                {store.metadata?.unitNumber && (
                                  <span className="flex items-center gap-1">
                                    <MapPin className="w-3 h-3" />
                                    Unit {store.metadata.unitNumber}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-4 ml-4">
                              <div className="flex items-center gap-1">
                                {store.metadata?.isFeatured && (
                                  <Star className="w-5 h-5 text-yellow-500 fill-current" />
                                )}
                              </div>
                              <div
                                className="flex items-center gap-2"
                                onClick={(e) => e?.stopPropagation()}
                              >
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/retail/${store.id}`)
                                  }
                                  icon={Eye}
                                  title="View details"
                                />
                                {user?.isAdmin && (
                                  <>
                                    <EnhancedIconButton
                                      onClick={() =>
                                        navigate(`/retail/${store.id}/edit`)
                                      }
                                      icon={Edit}
                                      title="Edit store"
                                    />
                                    <EnhancedIconButton
                                      onClick={() => handleDelete(store.id)}
                                      icon={Trash2}
                                      title="Delete store"
                                      variant="danger"
                                    />
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Table View */}
                {viewMode === "table" && (
                  <div className="w-full">
                    {/* Table Body - No internal scroll */}
                    <div className="divide-y divide-gray-200">
                      {filteredStores.map((store) => (
                        <div
                          key={store.id}
                          className="px-6 py-3 hover:bg-hover transition-colors cursor-pointer"
                          onClick={() => navigate(`/retail/${store.id}`)}
                        >
                          <div className="grid grid-cols-12 gap-4 items-start">
                            <div className="col-span-3">
                              <div className="text-sm font-medium text-gray-900">
                                {store.title}
                              </div>
                              <div className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {store.description}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <span className="text-sm text-gray-600">
                                {store.metadata?.category}
                              </span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-sm text-gray-600">
                                {store.metadata?.storeType?.replace("_", " ")}
                              </span>
                            </div>
                            <div className="col-span-1">
                              <div className="flex items-center gap-1">
                                {store.metadata?.isVerified && (
                                  <span className="px-2 py-1 text-xs font-medium rounded bg-green-100 text-green-800">
                                    Verified
                                  </span>
                                )}
                                {store.metadata?.isFeatured && (
                                  <Star className="w-4 h-4 text-yellow-500 fill-current" />
                                )}
                              </div>
                            </div>
                            <div className="col-span-2">
                              <span className="text-sm text-gray-600">
                                {store.metadata?.unitNumber
                                  ? `Unit ${store.metadata.unitNumber}`
                                  : "-"}
                              </span>
                            </div>
                            <div className={user?.isAdmin ? "col-span-1" : "col-span-2"}>
                              <span className="text-sm text-gray-500">
                                {store.author.name || store.author.email}
                              </span>
                            </div>
                            {user?.isAdmin && (
                              <div className="col-span-1 flex items-center justify-end gap-2" onClick={(e) => e?.stopPropagation()}>
                                <EnhancedIconButton
                                  onClick={() =>
                                    navigate(`/retail/${store.id}/edit`)
                                  }
                                  icon={Edit}
                                  title="Edit store"
                                />
                                <EnhancedIconButton
                                  onClick={() => handleDelete(store.id)}
                                  icon={Trash2}
                                  title="Delete store"
                                  variant="danger"
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
